import type { FallbackRender } from '@sentry/react'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useRef } from 'react'
import * as Sentry from '@sentry/react'
import { useLocation } from 'react-router-dom'

import { isChunkLoadError } from 'library/lazy-with-retry'

import { TechnicalErrorFallback } from './technical-error-fallback'

interface CheckoutErrorBoundaryProps {
    children: ReactNode
}

interface CatchLog {
    pathname: string
    count: number
}

const SOFT_RESET_LIMIT = 1

// A rejected lazy import stays rejected in React, and a repeated crash on the
// same route means a soft reset already failed: only a fresh document recovers.
// The persisted store brings the user back to the same step.
const needsHardReload = (error: unknown, catchesOnRoute: number): boolean =>
    isChunkLoadError(error) || catchesOnRoute > SOFT_RESET_LIMIT

// React 19 no longer forwards boundary-caught errors to window.onerror, so the
// Sentry boundary is what keeps render crashes visible in Sentry.
export const CheckoutErrorBoundary = ({ children }: CheckoutErrorBoundaryProps) => {
    const { pathname } = useLocation()
    const boundaryRef = useRef<Sentry.ErrorBoundary>(null)
    // Counted in the catch handler, not in an effect: componentDidCatch runs in
    // the same commit as a mount or route change, before passive effects.
    const catchLog = useRef<CatchLog>({ pathname, count: 0 })
    const isFallbackShown = useRef(false)

    // Browser back while the fallback is shown must not keep it on the new route.
    useEffect(() => {
        if (isFallbackShown.current && catchLog.current.pathname !== pathname) {
            boundaryRef.current?.resetErrorBoundary()
        }
    }, [pathname])

    const handleError = (error: unknown) => {
        const previous = catchLog.current
        catchLog.current = {
            pathname,
            count: previous.pathname === pathname ? previous.count + 1 : 1,
        }
        isFallbackShown.current = true
    }

    const handleReset = () => {
        isFallbackShown.current = false
    }

    // Stable identity matters: the Sentry boundary mounts this function as a
    // component, so a new function per render would remount the fallback.
    const renderFallback = useCallback<FallbackRender>(
        (fallbackProps) => (
            <TechnicalErrorFallback
                onRetry={() => {
                    // Reloads stay click-driven so the stale-chunk reload guard
                    // in lazy-with-retry is never bypassed.
                    if (needsHardReload(fallbackProps.error, catchLog.current.count)) {
                        window.location.reload()
                        return
                    }
                    fallbackProps.resetError()
                }}
            />
        ),
        [],
    )

    return (
        <Sentry.ErrorBoundary
            ref={boundaryRef}
            beforeCapture={(scope) => scope.setTag('errorBoundary', 'checkout')}
            onError={handleError}
            onReset={handleReset}
            fallback={renderFallback}
        >
            {children}
        </Sentry.ErrorBoundary>
    )
}
